import { useEffect, useState } from 'react';
import { Container, Button } from "react-bootstrap";
import { axiosPrivate } from "../api/axios";
import useAuth from "../hooks/useAuth";
import { Checkbox, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import Orders from "./Orders";

export default function Account() {
  const [isLoadingShipping, setIsLoadingShipping] = useState(true);
  const [isLoadingBilling, setIsLoadingBilling] = useState(true);
  const [isLoadingPickupAddresses, setIsLoadingPickupAddresses] = useState(true);
  const [isSavingPickupStates, setIsSavingPickupStates] = useState(false);

  const [address, setAddress] = useState({
    billing: {},
    shipping: {}
  });

  const { auth } = useAuth();

  const states = [
    'WA', 'VIC', 'QLD', 'ACT', 'SA', 'NSW', 'TAS'
  ];

  const [pickupStates, setPickupStates] = useState(states);
  const [tempPickupStates, setTempPickupStates] = useState(states);
  const [hasChanges, setHasChanges] = useState(false);

  const defaultAddress = async (type) => {
    try {
      const response = await axiosPrivate.get('/api/address/list/default/' + type)

      setAddress((prev) => ({ ...prev, [type]: response.data }));
    } catch (error) {
      if (error.response) {
        console.log(error.response.data);
      }
    }
  }

  const getPickupStates = async () => {
    try {
      const response = await axiosPrivate.get('/api/user/pickup');
      // if pickupStates data not null in db then set
      // else all states selected.
      if (response?.data?.length) {
        setPickupStates(response.data);
        setTempPickupStates(response.data);
      }
    } catch (err) {
      console.error("get pickup states error: ", err)
    }
  }

  const handlePickupStateChange = (event) => {
    const { value, checked } = event.target;

    setTempPickupStates(prev => {
      let newStates;
      if (checked) {
        newStates = [...prev, value];
      } else {
        newStates = prev.filter(state => state !== value);
      }

      const hasChanges = JSON.stringify(newStates.sort()) !== JSON.stringify(pickupStates.sort());
      setHasChanges(hasChanges);

      return newStates;
    });
  }

  const savePickupStates = async () => {
    setIsSavingPickupStates(true);
    setIsLoadingPickupAddresses(true);

    try {
      await axiosPrivate.post('/api/user/pickup/set', JSON.stringify(tempPickupStates));
      setPickupStates(tempPickupStates);
      setHasChanges(false);
    } catch (err) {
      console.error("save pickup states error: ", err.response?.data || err);
    } finally {
      setIsSavingPickupStates(false);
      setIsLoadingPickupAddresses(false);
    }
  }

  const resetPickupStates = () => {
    setTempPickupStates(pickupStates);
    setHasChanges(false);
  }

  useEffect(() => {
    setIsLoadingPickupAddresses(true);

    defaultAddress('shipping').then(() => (
      setIsLoadingShipping(false)
    ));

    defaultAddress('billing').then(() => (
      setIsLoadingBilling(false)
    ));

    getPickupStates().then(() => {
      setIsLoadingPickupAddresses(false);
    });
  }, []);

  return (
    <Container>
      <div className="column main mb-3">
        <div className="page-title-wrapper">
          <h1 className="page-title">My Account</h1>
        </div>
      </div>

      <div className="block block-dashboard-info">

        <div className="row mb-3 border-bottom pb-1">
          <div className="block-title col-12 col-sm-6 text-start"><strong>Account Information</strong></div>
          <div className="col-12 col-sm-6 text-end"></div>
        </div>


        <div className="row block-content mb-4">
          <div className="col box box-information">
            <div className="box-title">
              <span>Contact Information</span>
            </div>
            <div className="box-content">
              <p>
                Account No: {auth.accno}<br />
                Email: {auth.email}<br />
              </p>
            </div>
            <div className="box-actions">
              <Link onClick={() => alert("Change Password function arriving soon!")}><span>Change Password</span></Link>
            </div>
          </div>
          <div className="col box box-newsletter">
            <div className="box-title">
              <span>Pickup Addresses</span>
            </div>
            <div className="box-content">
              <p>Show pickup addresses from following Australian states</p>
            </div>
            <div className="box-actions">

              {
                isLoadingPickupAddresses ? <CircularProgress /> :

                  <div>
                    <form id="form-states" >
                      {states.map((key, id) => {
                        return (
                          <label key={id} className="pe-2">
                            <Checkbox
                              name="states"
                              checked={tempPickupStates?.includes(key) || false}
                              value={key}
                              onChange={handlePickupStateChange}
                              disabled={isLoadingPickupAddresses}
                            />{key}
                          </label>
                        );
                      })}
                    </form>

                    {/* Save/Reset buttons */}
                    {hasChanges && !isLoadingPickupAddresses && (
                      <div className="mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={savePickupStates}
                          disabled={isSavingPickupStates}
                          className="me-2"
                        >
                          {isSavingPickupStates ? (
                            <>
                              <CircularProgress size={16} className="me-1" />
                              Saving...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={resetPickupStates}
                          disabled={isSavingPickupStates}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
              }
            </div>
          </div>
        </div>
      </div>

      <div className="block block-dashboard-addresses">

        <div className="row mb-3 mt-5 border-bottom pb-1">
          <div className="block-title col-12 col-sm-6 text-start"><strong>Address Settings</strong></div>
          <div className="col-12 col-sm-6 text-end"><Link to="/address-book"><span>Address Book</span></Link></div>
        </div>

        <div className="row block-content mb-4 ">
          <div className="col box box-billing-address">
            <div className="box-title">
              <strong>Default Billing Address</strong>
            </div>
            <div className="box-content">
              {
                isLoadingShipping ? <CircularProgress /> : (
                  <article className="address-shipping">
                    {
                      address.shipping ?
                        <p>
                          {address.shipping.company}<br />
                          {address.shipping.street1} {address.shipping.street2}<br />
                          {address.shipping.suburb} {address.shipping.state} {address.shipping.postcode}
                        </p>
                        : <p><i>You can set a default address in your address book.</i></p>
                    }
                  </article>
                )
              }
            </div>
          </div>

          <div className="col box box-shipping-address">
            <div className="box-title">
              <strong>Default Shipping Address</strong>
            </div>
            <div className="box-content">
              {
                isLoadingBilling ? <CircularProgress /> : (
                  <article className="address-billing">
                    {
                      address.billing ?
                        <p>
                          {address.billing.company}<br />
                          {address.billing.street1} {address.billing.street2}<br />
                          {address.billing.suburb} {address.billing.state} {address.billing.postcode}
                        </p>
                        : <p><i>You can set a default address in your address book.</i></p>
                    }
                  </article>
                )
              }
            </div>
          </div>
        </div>
      </div>

      <br />

      <Orders summary={true} />

    </Container>
  );
}